/** Reachable purchase approval, supplier ordering and partial receipt workflow. */
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth";
import {
  type PurchaseRequest,
  useApprovePurchase,
  useCancelPurchase,
  useOrderPurchase,
  usePurchaseRequests,
  useReceivePurchase,
} from "@/features/inventory/procurementQueries";
import { FormDialog } from "@/shared/components/form-dialog";
import { EmptyState, ErrorState, LoadingState } from "@/shared/components/page";
import { LocationPicker, PartyPicker } from "@/shared/components/pickers";
import { formatQuantity } from "@/shared/lib/format";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Field, Input, Textarea } from "@/shared/ui/form";

type Action = "order" | "receive" | "cancel";

/** Form for supplier commitment, delivery receipt or cancellation reason. */
function PurchaseActionDialog({
  purchase,
  action,
  onClose,
}: {
  purchase: PurchaseRequest;
  action: Action;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const order = useOrderPurchase();
  const receive = useReceivePurchase();
  const cancel = useCancelPurchase();
  const form = useForm({
    initial: {
      supplier_id: "",
      reference: "",
      expected_delivery: "",
      unit_cost: "0",
      currency: "",
      location_id: "",
      quantity: String(Number(purchase.quantity) - Number(purchase.order?.received_quantity ?? 0)),
      reason: "",
    },
    idPrefix: "purchase",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      if (action === "order") {
        await order.mutateAsync({
          id: purchase.id,
          body: {
            supplier_id: v.supplier_id,
            reference: v.reference,
            expected_delivery: v.expected_delivery,
            unit_cost: Number(v.unit_cost),
            currency: v.currency,
          },
        });
      } else if (action === "receive" && purchase.order) {
        await receive.mutateAsync({
          id: purchase.order.id,
          body: {
            location_id: v.location_id,
            quantity: Number(v.quantity),
            reference: v.reference,
          },
        });
      } else {
        await cancel.mutateAsync({ id: purchase.id, reason: v.reason });
      }
    });
    if (ok) onClose();
  };
  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t(`inventory.procurement.${action}`)}
      description={`${purchase.work_order_code} · ${purchase.item_sku}`}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      {action === "order" ? (
        <>
          <Field
            id="purchase-supplier_id"
            label={t("inventory.procurement.supplier")}
            error={form.errors.supplier_id}
          >
            <PartyPicker
              id="purchase-supplier_id"
              role="vendor"
              value={form.values.supplier_id}
              onChange={(id) => form.set("supplier_id", id ?? "")}
              required
            />
          </Field>
          <Field
            id="purchase-expected_delivery"
            label={t("inventory.procurement.expectedDelivery")}
            error={form.errors.expected_delivery}
          >
            <Input type="date" {...form.field("expected_delivery")} required />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              id="purchase-unit_cost"
              label={t("inventory.unitCost")}
              error={form.errors.unit_cost}
            >
              <Input type="number" min="0" step="0.0001" {...form.field("unit_cost")} required />
            </Field>
            <Field
              id="purchase-currency"
              label={t("inventory.procurement.currency")}
              error={form.errors.currency}
            >
              <Input maxLength={3} {...form.field("currency")} required />
            </Field>
          </div>
        </>
      ) : null}
      {action === "receive" ? (
        <>
          <Field
            id="purchase-location_id"
            label={t("inventory.location")}
            error={form.errors.location_id}
          >
            <LocationPicker
              id="purchase-location_id"
              stockOnly
              value={form.values.location_id}
              onChange={(id) => form.set("location_id", id ?? "")}
              required
            />
          </Field>
          <Field id="purchase-quantity" label={t("app.quantity")} error={form.errors.quantity}>
            <Input type="number" min="0.001" step="0.001" {...form.field("quantity")} required />
          </Field>
        </>
      ) : null}
      {action !== "cancel" ? (
        <Field
          id="purchase-reference"
          label={t("inventory.procurement.reference")}
          error={form.errors.reference}
        >
          <Input {...form.field("reference")} required />
        </Field>
      ) : (
        <Field
          id="purchase-reason"
          label={t("inventory.procurement.reason")}
          error={form.errors.reason}
        >
          <Textarea {...form.field("reason")} required />
        </Field>
      )}
    </FormDialog>
  );
}

/** Purchasing work queue, also embedded alongside work-order requirements. */
export function ProcurementPanel({ workOrderId }: { workOrderId?: string }): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const list = usePurchaseRequests(workOrderId);
  const approve = useApprovePurchase();
  const [dialog, setDialog] = useState<{ purchase: PurchaseRequest; action: Action } | null>(null);
  if (list.query.isPending) return <LoadingState />;
  if (list.query.isError)
    return <ErrorState error={list.query.error} onRetry={() => void list.query.refetch()} />;
  return (
    <div className="space-y-3">
      {dialog ? <PurchaseActionDialog {...dialog} onClose={() => setDialog(null)} /> : null}
      {approve.isError ? <ErrorState error={approve.error} /> : null}
      {list.items.length === 0 ? <EmptyState message={t("inventory.procurement.empty")} /> : null}
      {list.items.map((purchase) => (
        <article key={purchase.id} className="min-w-0 space-y-2 rounded-md border p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <a
              className="min-w-0 max-w-full break-words font-medium underline"
              href={`/work-orders/${purchase.work_order_id}`}
            >
              {purchase.work_order_code} · {purchase.item_sku} · {purchase.item_name}
            </a>
            <span>
              {t(`inventory.procurement.status.${purchase.status}`)} ·{" "}
              {formatQuantity(purchase.quantity)}
            </span>
          </div>
          <p className="whitespace-pre-wrap break-words text-sm">{purchase.reason}</p>
          {purchase.approved_at ? (
            <p className="text-xs text-muted-foreground">
              {t("inventory.procurement.approvedAt")}: {purchase.approved_at}
            </p>
          ) : null}
          {purchase.order ? (
            <div className="space-y-1 text-sm">
              <p className="break-words">
                {purchase.order.reference} · {purchase.order.supplier_name}
              </p>
              <p>
                {t("inventory.procurement.expectedDelivery")}: {purchase.order.expected_delivery}
              </p>
              <p>
                {t("inventory.procurement.received")}:{" "}
                {formatQuantity(purchase.order.received_quantity)} /{" "}
                {formatQuantity(purchase.quantity)}
              </p>
              {purchase.order.receipts.map((receipt) => (
                <p key={receipt.id} className="break-words text-xs">
                  {receipt.reference} · {formatQuantity(receipt.quantity)} ·{" "}
                  {t("inventory.procurement.allocated")}:{" "}
                  {formatQuantity(receipt.allocated_quantity)}
                </p>
              ))}
            </div>
          ) : null}
          {purchase.cancellation_reason ? (
            <p className="break-words text-sm">{purchase.cancellation_reason}</p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {purchase.status === "submitted" && can("procurement.request.approve") ? (
              <Button
                size="sm"
                disabled={approve.isPending}
                onClick={() => approve.mutate(purchase.id)}
              >
                {t("inventory.procurement.approve")}
              </Button>
            ) : null}
            {purchase.status === "approved" && can("procurement.order.manage") ? (
              <Button size="sm" onClick={() => setDialog({ purchase, action: "order" })}>
                {t("inventory.procurement.order")}
              </Button>
            ) : null}
            {purchase.status === "ordered" && can("procurement.receipt.create") ? (
              <Button size="sm" onClick={() => setDialog({ purchase, action: "receive" })}>
                {t("inventory.procurement.receive")}
              </Button>
            ) : null}
            {!["received", "cancelled"].includes(purchase.status) &&
            can("procurement.order.manage") ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDialog({ purchase, action: "cancel" })}
              >
                {t("inventory.procurement.cancel")}
              </Button>
            ) : null}
          </div>
        </article>
      ))}
      {list.hasMore ? (
        <Button variant="outline" onClick={list.loadMore} disabled={list.isLoadingMore}>
          {t("app.loadMore")}
        </Button>
      ) : null}
    </div>
  );
}
